import React from 'react';
import { Box, Button, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import FundInvestmentInvestor from '../../../models/admin/FundInvestmentData';
import { STATUS_LABELS } from './constants';
import type { BulkPromoteInvestorItem } from './investorManagementTypes';
import { getNextStage } from './investorPromotionUtils';
import StyledModal from '../../styles/StyledModal';

export interface InvestorBulkPromoteModalProps {
  open: boolean;
  stage?: FundInvestmentInvestor['status'];
  investors?: BulkPromoteInvestorItem[];
  onClose: () => void;
  onSubmit: () => void;
  onUpdateAmount: (
    id: string,
    field: 'preferredInvestment' | 'committedInvestment',
    value: string,
  ) => void;
  onRemoveInvestor: (id: string) => void;
}

export default function InvestorBulkPromoteModal({
  open,
  stage,
  investors,
  onClose,
  onSubmit,
  onUpdateAmount,
  onRemoveInvestor,
}: InvestorBulkPromoteModalProps) {
  if (!stage) {
    return null;
  }

  let nextStageLabel = '';
  try {
    const nextStage = getNextStage(stage);
    nextStageLabel = STATUS_LABELS[nextStage] || nextStage;
  } catch (error) {
    console.error('Error getting next stage:', error);
    nextStageLabel = 'Unknown Stage';
  }

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title={`Bulk Promote ${STATUS_LABELS[stage] || stage} to ${nextStageLabel}`}
      actions={
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmit}>
            Promote All
          </Button>
        </Box>
      }
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        Review and update investment amounts for promotion:
      </Typography>

      <Stack spacing={2}>
        {investors?.map((item) => (
          <Paper key={item.id} sx={{ p: 2, border: '1px solid #ddd' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">{item.investor.name || 'Unknown Contact'}</Typography>
              <IconButton onClick={() => onRemoveInvestor(item.id)} size="small">
                <RemoveIcon />
              </IconButton>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                type="number"
                label="Preferred Investment"
                value={item.preferredInvestment || ''}
                onChange={(e) => onUpdateAmount(item.id, 'preferredInvestment', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px' }}>$</span>,
                }}
              />
              <TextField
                type="number"
                label="Committed Investment"
                value={item.committedInvestment || ''}
                onChange={(e) => onUpdateAmount(item.id, 'committedInvestment', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px' }}>$</span>,
                }}
              />
            </Box>
          </Paper>
        ))}
      </Stack>
    </StyledModal>
  );
}
